/**
 * The day the office picked for a payment, as the instant we store.
 *
 * `occurred_at` is what the month-end reports filter on, so the only thing
 * that matters about it is which CALENDAR DAY it lands on. A date input hands
 * back a bare 'YYYY-MM-DD' with no zone, and the obvious way to turn that
 * into an ISO string is the wrong one:
 *
 *     new Date(`${ymd}T00:00:00`).toISOString()
 *
 * An ISO date-TIME with no offset is parsed as LOCAL time, so on a Budapest
 * browser 2026-09-16 became '2026-09-15T22:00:00.000Z' — the previous UTC
 * day. A payment entered on the 1st was filed in the month before, and one
 * entered on 1 January was filed in the year before. Four call sites built
 * this string and one of them had no `Z`; this exists so there is one.
 *
 * Nothing here consults the host timezone: the day the office typed is the
 * day that gets stored, from Budapest, UTC or anywhere else.
 */

/** A bare calendar date, as every date input in the app emits it. */
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Midnight UTC on `ymd`, as an ISO-8601 string.
 *
 * @param {string} ymd - A calendar date, 'YYYY-MM-DD'. A longer ISO string is
 *   accepted and read down to its date part, so a value round-tripped from
 *   the API works without the caller slicing it first.
 * @returns {string} e.g. '2026-09-16T00:00:00.000Z'. Falls back to the
 *   current instant when `ymd` is missing, malformed, or a date that does not
 *   exist ('2026-02-30') — the behaviour the partner modal already had, and
 *   better than the `RangeError` the other three threw on an empty input.
 */
export default function occurredAtIso(ymd) {
  const datePart = String(ymd ?? '').slice(0, 10);
  if (!YMD.test(datePart)) {
    return new Date().toISOString();
  }

  // Explicitly UTC — this is the whole point of the helper.
  const instant = Date.parse(`${datePart}T00:00:00.000Z`);
  if (!Number.isFinite(instant)) {
    return new Date().toISOString();
  }

  const iso = new Date(instant).toISOString();

  /*
   * `Date.parse` rejects an impossible MONTH but quietly rolls an impossible
   * DAY forward: '2026-02-30' comes back as 2 March. That is the same class
   * of defect this helper exists to stop — a payment crossing a month
   * boundary on its own — so the day we produce has to be the day we were
   * given, or we did not understand the input.
   */
  if (iso.slice(0, 10) !== datePart) {
    return new Date().toISOString();
  }

  return iso;
}
